import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Link,
    Typography
} from "@mui/material";
import React from "react";
import UtilService from "../../../services/UtilService";

/** Stripe API often uses Unix seconds; app uses ms — normalize for UtilService.formatDate. */
function stripeTimeToMillis(ts) {
    if (ts == null) return null;
    const n = Number(ts);
    if (!Number.isFinite(n) || n === 0) return null;
    return n < 1e12 ? n * 1000 : n;
}

/**
 * CurrentSubs Component
 * 
 * Displays the current subscription details for a user.
 * 
 * @param {Object} currSub - The current subscription object
 * @param {boolean|null|undefined} stripeLivemode - User's Stripe live mode (true = production)
 * @param {string|undefined} stripeCustomerId - Stripe customer id (cus_...) for live API calls
 * @param {function} onForceEndClick - Callback function when Force End button is clicked
 * @param {boolean} forceEnding - Indicates if force end operation is in progress
 * @param {function} onCheckStripeClick - Check subscription on Stripe (API when live, dashboard when test)
 * @param {function} [onCancelStripeClick] - Test mode only: opens confirm / Stripe dashboard for cancel
 * @param {boolean} stripeChecking - Check-on-Stripe in progress
 * @param {boolean} [stripeCanceling] - Cancel-on-Stripe in progress (test mode button)
 */
const CurrentSubs = ({
    currSub,
    stripeLivemode,
    stripeCustomerId,
    onForceEndClick,
    forceEnding,
    onCheckStripeClick,
    onCancelStripeClick,
    stripeChecking,
    stripeCanceling,
}) => {
    if (!currSub) {
        return null;
    }

    const formatCurrency = (amount, currency) => {
        if (amount == null || currency == null || String(currency).trim() === "") return "N/A";
        return new Intl.NumberFormat("en-CA", {
            style: "currency",
            currency: String(currency).toUpperCase(),
        }).format(amount);
    };

    const invSucceeded = currSub.invoicePaymentSucceeded?.data?.object;
    const invPaidEvt = currSub.invoicePaymentPaid?.data?.object;
    const invLineDesc = invSucceeded?.lines?.data?.[0]?.description;

    const lastPaymentDollars =
        currSub.lastPaymentAmount != null && currSub.lastPaymentAmount !== ""
            ? Number(currSub.lastPaymentAmount)
            : invSucceeded?.amount_paid != null || invSucceeded?.total != null
                ? Number(invSucceeded.amount_paid ?? invSucceeded.total) / 100
                : invPaidEvt?.amount_paid != null
                    ? Number(invPaidEvt.amount_paid) / 100
                    : null;

    const lastPaymentCurrency =
        currSub.lastPaymentCurrency ||
        invSucceeded?.currency ||
        invPaidEvt?.currency ||
        null;

    const transactionDisplay =
        currSub.lastPaymentTransactionId ||
        invPaidEvt?.payment?.payment_intent ||
        invSucceeded?.id ||
        null;

    const paymentMethodDisplay =
        currSub.lastPaymentMethod ||
        (invPaidEvt?.payment?.type === "payment_intent" ? "card (Stripe)" : null) ||
        "N/A";

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Current Subscription
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Status
                        </Typography>
                        <Chip
                            label={currSub.status || "N/A"}
                            color={
                                currSub.status === "ACTIVE"
                                    ? "success"
                                    : currSub.status === "INACTIVE"
                                        ? "error"
                                        : currSub.status === "CANCELED" || currSub.status === "CANCELLED"
                                            ? "warning"
                                            : "default"
                            }
                            size="small"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Start Date
                        </Typography>
                        <Typography variant="body1">
                            {UtilService.formatDate(currSub.startAt) || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            End Date
                        </Typography>
                        <Typography variant="body1">
                            {UtilService.formatDate(currSub.endAt) || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Last Payment
                        </Typography>
                        <Typography variant="body1">
                            {formatCurrency(lastPaymentDollars, lastPaymentCurrency)}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Payment Method
                        </Typography>
                        <Typography variant="body1">{paymentMethodDisplay}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Payment Status
                        </Typography>
                        {currSub.lastPaymentStatus ? (
                            <Chip
                                label={currSub.lastPaymentStatus}
                                color={
                                    currSub.lastPaymentStatus === "SUCCESS" ||
                                        currSub.lastPaymentStatus === "COMPLETED"
                                        ? "success"
                                        : currSub.lastPaymentStatus === "FAILED" ||
                                            currSub.lastPaymentStatus === "ERROR"
                                            ? "error"
                                            : "default"
                                }
                                size="small"
                            />
                        ) : invSucceeded?.status ? (
                            <Chip
                                label={invSucceeded.status}
                                color={invSucceeded.status === "paid" ? "success" : "default"}
                                size="small"
                            />
                        ) : (
                            <Typography variant="body1">N/A</Typography>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Transaction / invoice ID
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                        >
                            {transactionDisplay || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Stripe invoice ID
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                        >
                            {currSub.invoiceId || "—"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Canceled at
                        </Typography>
                        <Typography variant="body1">
                            {currSub.canceledAt != null && currSub.canceledAt !== 0
                                ? UtilService.formatDate(currSub.canceledAt) || "N/A"
                                : "—"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Subscription Stripe live mode
                        </Typography>
                        {currSub.stripeLivemode == null ? (
                            <Typography variant="body1">—</Typography>
                        ) : (
                            <Chip
                                label={currSub.stripeLivemode ? "Live" : "Test"}
                                color={currSub.stripeLivemode ? "success" : "default"}
                                size="small"
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Stripe subscription ID
                        </Typography>
                        <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                            {currSub.stripeSubscriptionId || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Created At
                        </Typography>
                        <Typography variant="body1">
                            {UtilService.formatDate(currSub.createdAt) || "N/A"}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                            Updated At
                        </Typography>
                        <Typography variant="body1">
                            {currSub.updatedAt
                                ? UtilService.formatDate(currSub.updatedAt) || "N/A"
                                : "N/A"}
                        </Typography>
                    </Grid>
                </Grid>

                {(invSucceeded || invPaidEvt) && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Latest invoice (from Stripe webhooks)
                        </Typography>
                        <Grid container spacing={2}>
                            {invSucceeded?.number && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Invoice number
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                                        {invSucceeded.number}
                                    </Typography>
                                </Grid>
                            )}
                            {invSucceeded && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Invoice amount (webhook)
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatCurrency(
                                            (invSucceeded.amount_paid ?? invSucceeded.total) != null
                                                ? Number(invSucceeded.amount_paid ?? invSucceeded.total) / 100
                                                : null,
                                            invSucceeded.currency
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                            {invSucceeded?.status && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Invoice status
                                    </Typography>
                                    <Chip label={invSucceeded.status} size="small" color="default" />
                                </Grid>
                            )}
                            {invSucceeded?.created != null && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Invoice created (Stripe)
                                    </Typography>
                                    <Typography variant="body1">
                                        {UtilService.formatDate(stripeTimeToMillis(invSucceeded.created)) || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {invLineDesc && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Line item
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                        {invLineDesc}
                                    </Typography>
                                </Grid>
                            )}
                            {invPaidEvt && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Invoice payment status
                                    </Typography>
                                    <Chip
                                        label={invPaidEvt.status || "N/A"}
                                        color={invPaidEvt.status === "paid" ? "success" : "default"}
                                        size="small"
                                    />
                                </Grid>
                            )}
                            {invPaidEvt?.payment?.payment_intent && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        PaymentIntent
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontFamily: "monospace", fontSize: "0.875rem", wordBreak: "break-all" }}
                                    >
                                        {invPaidEvt.payment.payment_intent}
                                    </Typography>
                                </Grid>
                            )}
                            {(invSucceeded?.hosted_invoice_url || invSucceeded?.invoice_pdf) && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Stripe links
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                        {invSucceeded.hosted_invoice_url && (
                                            <Link
                                                href={invSucceeded.hosted_invoice_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Hosted invoice
                                            </Link>
                                        )}
                                        {invSucceeded.invoice_pdf && (
                                            <Link
                                                href={invSucceeded.invoice_pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Invoice PDF
                                            </Link>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}

                <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-start" }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={onCheckStripeClick}
                        disabled={
                            stripeChecking ||
                            !currSub.id ||
                            (stripeLivemode === true &&
                                (!stripeCustomerId || String(stripeCustomerId).trim() === "")) ||
                            (stripeLivemode !== true && !currSub.stripeSubscriptionId)
                        }
                    >
                        {stripeChecking ? "Checking…" : "Check on Stripe"}
                    </Button>
                    {stripeLivemode !== true && onCancelStripeClick && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={onCancelStripeClick}
                            disabled={
                                stripeCanceling ||
                                !currSub.id ||
                                !currSub.stripeSubscriptionId
                            }
                        >
                            {stripeCanceling ? "Canceling…" : "Cancel on Stripe"}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={onForceEndClick}
                        disabled={forceEnding}
                    >
                        Force End
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default CurrentSubs;

